import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  Cell,
} from 'recharts';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Users } from 'lucide-react';

interface HoursByUser {
  userId: number;
  userName: string;
  hours: number;
}

interface TeamMemberChartProps {
  data: HoursByUser[];
  className?: string;
}

const COLORS = [
  '#f97316', // orange
  '#3b82f6', // blue
  '#22c55e', // green
  '#8b5cf6', // purple
  '#ec4899', // pink
  '#14b8a6', // teal
  '#f59e0b', // amber
  '#6366f1', // indigo
];

export const TeamMemberChart: React.FC<TeamMemberChartProps> = ({
  data,
  className,
}) => {
  const { t } = useTranslation();

  if (data.length === 0) {
    return (
      <Card className={className}>
        <CardHeader className="pb-2">
          <CardTitle className="flex items-center gap-2 text-base">
            <Users className="h-4 w-4 text-orange-500" />
            {t('dashboard.hoursByTeamMember', 'Hours by Team Member')}
          </CardTitle>
        </CardHeader>
        <CardContent className="h-[200px] flex items-center justify-center text-muted-foreground">
          {t('dashboard.noTeamData', 'No team data available')}
        </CardContent>
      </Card>
    );
  }

  const chartData = data.map((d) => ({
    name: d.userName,
    hours: d.hours,
  }));

  return (
    <Card className={className}>
      <CardHeader className="pb-2">
        <CardTitle className="flex items-center gap-2 text-base">
          <Users className="h-4 w-4 text-orange-500" />
          {t('dashboard.hoursByTeamMember', 'Hours by Team Member')}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <ResponsiveContainer width="100%" height={Math.max(200, data.length * 40)}>
          <BarChart
            data={chartData}
            layout="vertical"
            margin={{ top: 5, right: 30, left: 0, bottom: 5 }}
          >
            <XAxis type="number" tickFormatter={(v) => `${v}h`} />
            <YAxis
              type="category"
              dataKey="name"
              width={120}
              tick={{ fontSize: 12 }}
              tickFormatter={(v) => v.length > 15 ? `${v.substring(0, 15)}...` : v}
            />
            <Tooltip
              formatter={(value) => [`${Number(value).toFixed(1)}h`, t('dashboard.hours', 'Hours')]}
              contentStyle={{
                backgroundColor: 'hsl(var(--background))',
                border: '1px solid hsl(var(--border))',
                borderRadius: '6px',
              }}
            />
            <Bar dataKey="hours" radius={[0, 4, 4, 0]}>
              {chartData.map((_, index) => (
                <Cell
                  key={`cell-${index}`}
                  fill={COLORS[index % COLORS.length]}
                />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </CardContent>
    </Card>
  );
};

export default TeamMemberChart;
